"use client";

import React, { FC, useCallback, useState } from "react";
import type { ToolComponentProps } from "@zapier/ai-toolkit";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import type { ToolInvocationUIPart } from "@ai-sdk/ui-utils";
import { Alert, AlertDescription, Button, Text } from "@zapier/design-system";

/**
 * Type guard to check if a message part is a ToolInvocationPart
 */
function isToolInvocationPart(part: unknown): part is ToolInvocationUIPart {
  return (
    typeof part === "object" &&
    part !== null &&
    (part as { type?: string }).type === "tool-invocation" &&
    "toolInvocation" in (part as Record<string, unknown>)
  );
}

/**
 * Markdown component for rendering formatted content
 */
const MarkdownPreview: FC<{ content: string }> = ({ content }) => {
  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
        fontFamily: "monospace",
        fontSize: "14px",
        whiteSpace: "pre-wrap",
        lineHeight: "1.5",
      }}
    >
      {content}
    </div>
  );
};

/**
 * Component for showing ownership status and toggle
 */
const OwnershipIndicator: FC<{
  isPartnerOwned: boolean;
  ownershipReason?: string;
  canToggle?: boolean;
  onToggle?: () => void;
}> = ({ isPartnerOwned, ownershipReason, canToggle, onToggle }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px",
        backgroundColor: isPartnerOwned ? "#fff3cd" : "#d1ecf1",
        borderRadius: "4px",
        marginBottom: "16px",
      }}
    >
      <div style={{ flex: 1 }}>
        <Text type="Body2" fontWeight={700}>
          {isPartnerOwned ? "🔒 Partner-Owned" : "🏠 Zapier-Owned"}
        </Text>
        {ownershipReason && (
          <Text type="MinimalPrint1" color="GrayWarm6">
            {ownershipReason}
          </Text>
        )}
      </div>
      {canToggle && (
        <Button variant="secondary" size="small" onClick={onToggle}>
          Switch to {isPartnerOwned ? "Internal" : "Partner"} Format
        </Button>
      )}
    </div>
  );
};

/**
 * ConfirmLokiIssue Component with ownership toggle support
 */
export const ConfirmLokiIssue: FC<ToolComponentProps<any, any>> = ({
  args,
  part,
}) => {
  const [activeTab, setActiveTab] = useState<"preview" | "fields">("preview");
  const { append, messages, setMessages } = useCopilotChat();

  const issueArgs = args as any;
  const { toolInvocation } = part as unknown as ToolInvocationUIPart;
  const toolCallId = (toolInvocation as { toolCallId?: string }).toolCallId;

  // Check if this is in the latest message
  const latestMessage = messages[messages.length - 1];
  const isInLatestMessage = latestMessage?.parts?.some(
    (part) =>
      isToolInvocationPart(part) &&
      part.toolInvocation.toolCallId === toolCallId,
  );

  /**
   * Handler to toggle ownership and regenerate
   */
  const handleToggleOwnership = useCallback(() => {
    if (!issueArgs?.canToggleOwnership) return;

    // Check if we have the data to regenerate (either bugData or featureData)
    const hasData = issueArgs.bugData || issueArgs.featureData;
    if (!hasData) {
      console.error("No data available for regeneration");
      return;
    }

    // Send a clear message that triggers the regeneration mode
    const regenerateMessage = {
      role: "user" as const,
      content: `Switch this to ${issueArgs.isPartnerOwned ? "internal" : "partner"} format.`,
    };

    append(regenerateMessage).catch((error) => {
      console.error("Failed to regenerate with different ownership:", error);
    });
  }, [append, issueArgs]);

  /**
   * Handler for creating the issue
   */
  const handleCreateIssue = useCallback(() => {
    if (!toolCallId) return;

    const updatedMessages = [...messages];
    const lastMessage = updatedMessages[updatedMessages.length - 1];

    const invocationPart = lastMessage.parts?.find(
      (part): part is ToolInvocationUIPart =>
        isToolInvocationPart(part) &&
        part.toolInvocation.toolCallId === toolCallId,
    );

    if (invocationPart) {
      const toolResult = {
        ...invocationPart.toolInvocation,
        state: "result" as const,
        result: {
          success: true,
          action: "create",
          userApproved: true,
          issueData: issueArgs,
        },
      };

      invocationPart.toolInvocation = toolResult;

      if (lastMessage.toolInvocations) {
        lastMessage.toolInvocations = lastMessage.toolInvocations.map(
          (inv: any) => (inv.toolCallId === toolCallId ? toolResult : inv),
        );
      }

      setMessages(updatedMessages);
    }

    const createMessage = {
      role: "user" as const,
      content: `Create the ${issueArgs?.type || "issue"} as shown above.`,
    };

    append(createMessage).catch((error) => {
      console.error("Failed to create issue:", error);
    });
  }, [append, messages, setMessages, toolCallId, issueArgs]);

  // Early return if args not available
  if (!issueArgs) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          backgroundColor: "white",
          border: "1px solid #e5e5e5",
          borderRadius: "8px",
          margin: "16px 0",
        }}
      >
        <Text>Loading issue preview...</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "1px solid #e5e5e5",
        borderRadius: "8px",
        padding: "24px",
        margin: "16px 0",
      }}
    >
      {/* Header with issue type and app */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "18px" }}>
            {issueArgs?.type === "bug" ? "🐛" : "⭐"}
          </span>
          <Text type="SectionHeader">
            {issueArgs?.type === "bug" ? "Bug Report" : "Feature Request"}{" "}
            Preview
          </Text>
          <span
            style={{
              padding: "2px 8px",
              backgroundColor: "#e5e5e5",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            {issueArgs?.affectedApp || "Unknown App"}
          </span>
        </div>
      </div>

      {/* Ownership Indicator with Toggle */}
      {issueArgs?.displayOwnershipToggle && (
        <OwnershipIndicator
          isPartnerOwned={issueArgs.isPartnerOwned}
          ownershipReason={issueArgs.ownershipReason}
          canToggle={issueArgs.canToggleOwnership}
          onToggle={handleToggleOwnership}
        />
      )}

      {/* Title */}
      <div style={{ marginBottom: "16px" }}>
        <Text type="Body2" fontWeight={700} style={{ marginBottom: "4px" }}>
          Title:
        </Text>
        <Text type="Body1">{issueArgs?.title || "No title"}</Text>
      </div>

      {/* Validation Messages */}
      {issueArgs?.validationErrors && issueArgs.validationErrors.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <Alert status="error">
            <AlertDescription>
              <Text fontWeight={700}>Errors must be fixed:</Text>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                {issueArgs.validationErrors.map((error: string, i: number) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {issueArgs?.validationWarnings &&
        issueArgs.validationWarnings.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <Alert status="warning">
              <AlertDescription>
                <Text fontWeight={700}>Warnings (optional):</Text>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  {issueArgs.validationWarnings.map(
                    (warning: string, i: number) => (
                      <li key={i}>{warning}</li>
                    ),
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        )}

      {/* Content Tabs */}
      <div>
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "16px",
            borderBottom: "1px solid #e5e5e5",
          }}
        >
          <button
            onClick={() => setActiveTab("preview")}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "preview"
                  ? "2px solid #1a73e8"
                  : "2px solid transparent",
              cursor: "pointer",
              fontWeight: activeTab === "preview" ? 600 : 400,
            }}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab("fields")}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "fields"
                  ? "2px solid #1a73e8"
                  : "2px solid transparent",
              cursor: "pointer",
              fontWeight: activeTab === "fields" ? 600 : 400,
            }}
          >
            Issue Details
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ padding: "16px 0" }}>
          {activeTab === "preview" && (
            <MarkdownPreview content={issueArgs?.body || ""} />
          )}

          {activeTab === "fields" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div>
                <Text type="MinimalPrint1" color="GrayWarm6">
                  Type:
                </Text>
                <Text>
                  {issueArgs?.type === "bug" ? "Bug Report" : "Feature Request"}
                </Text>
              </div>
              <div>
                <Text type="MinimalPrint1" color="GrayWarm6">
                  App:
                </Text>
                <Text>{issueArgs?.affectedApp || "Unknown"}</Text>
              </div>
              <div>
                <Text type="MinimalPrint1" color="GrayWarm6">
                  API:
                </Text>
                <Text>{issueArgs?.selectedApi || "Unknown"}</Text>
              </div>
              <div>
                <Text type="MinimalPrint1" color="GrayWarm6">
                  Ownership:
                </Text>
                <Text>
                  {issueArgs?.isPartnerOwned ? "Partner-Owned" : "Zapier-Owned"}
                </Text>
              </div>
              {issueArgs?.ownershipReason && (
                <div>
                  <Text type="MinimalPrint1" color="GrayWarm6">
                    Ownership Reason:
                  </Text>
                  <Text>{issueArgs.ownershipReason}</Text>
                </div>
              )}
              <div>
                <Text type="MinimalPrint1" color="GrayWarm6">
                  Affected Users:
                </Text>
                <Text>{issueArgs?.affectedUsers?.length || 0} user(s)</Text>
              </div>
              {issueArgs?.frequency && (
                <div>
                  <Text type="MinimalPrint1" color="GrayWarm6">
                    Frequency:
                  </Text>
                  <Text>{issueArgs.frequency}</Text>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {isInLatestMessage && (
        <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
          <Button
            variant="primary"
            size="medium"
            onClick={handleCreateIssue}
            isDisabled={
              issueArgs?.validationErrors &&
              issueArgs.validationErrors.length > 0
            }
          >
            Create {issueArgs?.type === "bug" ? "Bug" : "Feature Request"}
          </Button>

          {issueArgs?.canToggleOwnership && (
            <Button
              variant="secondary"
              size="medium"
              onClick={handleToggleOwnership}
            >
              Switch to {issueArgs?.isPartnerOwned ? "Internal" : "Partner"}{" "}
              Format
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
